import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Hash, ChevronLeft, History, Trash2, Eye, GitCompare, Loader2, Search, Calendar, User, Star, Plus } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryCard = ({ entry, onView, onDelete, onSelect, isSelected }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`
      p-4 rounded-xl border-2 transition-all cursor-pointer
      ${isSelected 
        ? 'border-emerald-500 bg-emerald-50' 
        : 'border-gray-200 bg-white hover:border-emerald-200'}
    `}
    onClick={() => onSelect(entry.id)}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <User className="h-4 w-4 text-gray-400" />
          <span className="font-semibold text-gray-800">
            {entry.first_name} {entry.last_name}
          </span>
          {entry.label && (
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
              {entry.label}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(entry.birth_date).toLocaleDateString('fr-FR')}
          </span>
          <span className="text-xs">
            Sauvegardé le {new Date(entry.created_at).toLocaleDateString('fr-FR')}
          </span>
        </div>
        
        {/* Key Numbers */}
        <div className="flex gap-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 rounded-lg">
            <span className="text-xs text-emerald-600">Chemin</span>
            <span className="font-bold text-emerald-700">{entry.life_path}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded-lg">
            <span className="text-xs text-purple-600">Expression</span>
            <span className="font-bold text-purple-700">{entry.expression}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-lg">
            <span className="text-xs text-amber-600">Âme</span>
            <span className="font-bold text-amber-700">{entry.soul}</span>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col gap-2 ml-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onView(entry.id); }}
          className="h-8 w-8 p-0"
        >
          <Eye className="h-4 w-4 text-gray-500" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
          className="h-8 w-8 p-0 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 text-red-400" />
        </Button>
      </div>
    </div>
    
    {isSelected && (
      <div className="mt-3 pt-3 border-t border-emerald-200 text-center">
        <span className="text-xs text-emerald-600">✓ Sélectionné pour comparaison</span>
      </div>
    )}
  </motion.div>
);

const ComparisonView = ({ themes, onClose }) => {
  if (!themes || themes.length < 2) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-emerald-600" />
            Comparaison des thèmes
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>
        
        {/* Themes Grid */}
        <div className={`grid grid-cols-${Math.min(themes.themes?.length || 2, 4)} gap-4 mb-6`}>
          {themes.themes?.map((theme, i) => (
            <div key={theme.id} className="p-4 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-800 mb-1">
                {theme.first_name} {theme.last_name}
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                {new Date(theme.birth_date).toLocaleDateString('fr-FR')}
              </p>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Chemin de vie</span>
                  <span className="font-bold text-emerald-600 text-lg">{theme.life_path}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Expression</span>
                  <span className="font-bold text-purple-600 text-lg">{theme.expression}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Âme</span>
                  <span className="font-bold text-amber-600 text-lg">{theme.soul}</span>
                </div>
                {theme.personality && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Réalisation</span>
                    <span className="font-bold text-blue-600 text-lg">{theme.personality}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Insights */}
        {themes.insights && (
          <div className="space-y-4">
            {themes.insights.common_numbers?.length > 0 && (
              <div className="p-4 bg-emerald-50 rounded-xl">
                <h4 className="font-semibold text-emerald-800 mb-2">Points communs</h4>
                {themes.insights.common_numbers.map((item, i) => (
                  <p key={i} className="text-sm text-emerald-700">
                    <span className="font-medium">{item.type} {item.number}</span>: {item.note}
                  </p>
                ))}
              </div>
            )}
            
            {themes.insights.compatibility_notes?.length > 0 && (
              <div className="p-4 bg-purple-50 rounded-xl">
                <h4 className="font-semibold text-purple-800 mb-2">Compatibilité</h4>
                {themes.insights.compatibility_notes.map((note, i) => (
                  <p key={i} className="text-sm text-purple-700">{note}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const NumerologiaHistoryPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchHistory();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/numerologia/history/${user.id || user._id}`);
      const data = await response.json();
      
      if (data.success) {
        setHistory(data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (themeId) => {
    navigate(`/numerologia/history/${themeId}`);
  };

  const handleDelete = async (themeId) => {
    if (!confirm('Supprimer ce thème de l\'historique ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/history/${themeId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setHistory(history.filter(h => h.id !== themeId));
        setSelectedIds(selectedIds.filter(id => id !== themeId));
        toast.success('Thème supprimé');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else if (selectedIds.length < 4) {
      setSelectedIds([...selectedIds, id]);
    } else {
      toast.info('Maximum 4 thèmes pour la comparaison');
    }
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) {
      toast.error('Sélectionnez au moins 2 thèmes à comparer');
      return;
    }
    
    setLoadingCompare(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/history/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedIds)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setComparison(data.comparison);
      }
    } catch (error) {
      console.error('Error comparing:', error);
      toast.error('Erreur lors de la comparaison');
    } finally {
      setLoadingCompare(false);
    }
  };

  const filteredHistory = history.filter(entry => {
    const search = searchTerm.toLowerCase();
    return (
      entry.first_name?.toLowerCase().includes(search) ||
      entry.last_name?.toLowerCase().includes(search) ||
      entry.label?.toLowerCase().includes(search)
    );
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center p-8">
          <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold text-gray-700 mb-2">Connexion requise</h2>
          <p className="text-gray-500 mb-4">Connectez-vous pour accéder à votre historique</p>
          <Button onClick={() => navigate('/auth')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/numerologia')}
              className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-emerald-600" />
              <span className="font-heading text-lg font-bold text-emerald-700">
                Historique
              </span>
            </div>
          </div>
          
          <Button
            onClick={() => navigate('/numerologia/theme')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau thème
          </Button>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Search & Actions Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {selectedIds.length > 0 && (
              <Button
                onClick={handleCompare}
                disabled={selectedIds.length < 2 || loadingCompare}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
              >
                {loadingCompare ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <GitCompare className="h-4 w-4 mr-2" />
                )}
                Comparer ({selectedIds.length})
              </Button>
            )}
          </div>

          {/* Selection Info */}
          {selectedIds.length > 0 && (
            <div className="mb-4 p-3 bg-emerald-50 rounded-xl text-sm text-emerald-700 flex items-center justify-between">
              <span>{selectedIds.length} thème(s) sélectionné(s)</span>
              <button 
                onClick={() => setSelectedIds([])}
                className="text-emerald-600 hover:text-emerald-800 underline"
              >
                Tout désélectionner
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
              <p className="text-gray-500">Chargement de l'historique...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && history.length === 0 && (
            <div className="text-center py-12">
              <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-gray-700 mb-2">
                Aucun thème sauvegardé
              </h2>
              <p className="text-gray-500 mb-6">
                Calculez votre premier thème numérologique et sauvegardez-le !
              </p>
              <Button
                onClick={() => navigate('/numerologia/theme')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
              >
                <Star className="h-4 w-4 mr-2" />
                Calculer mon thème
              </Button>
            </div>
          )}

          {/* History List */}
          {!loading && filteredHistory.length > 0 && (
            <div className="space-y-4">
              {filteredHistory.map((entry) => (
                <HistoryCard
                  key={entry.id}
                  entry={entry}
                  onView={handleView}
                  onDelete={handleDelete}
                  onSelect={handleSelect}
                  isSelected={selectedIds.includes(entry.id)}
                />
              ))}
            </div>
          )}

          {/* No results */}
          {!loading && history.length > 0 && filteredHistory.length === 0 && (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun résultat pour "{searchTerm}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Modal */}
      <AnimatePresence>
        {comparison && (
          <ComparisonView 
            themes={comparison} 
            onClose={() => setComparison(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default NumerologiaHistoryPage;
